import {ReloadOutlined, SearchOutlined} from '@ant-design/icons/lib';
import {Button} from 'antd';
import {PaymentMethods, StoreFieldsFragment} from 'api/generated';
import {SearchBar} from 'components';
import {FilterInput} from 'components/FilterInput';
import {Formik, FormikHelpers} from 'formik';
import {DatePicker, Form, Select} from 'formik-antd';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {amountToInt} from 'lib/utils';
import moment, {Moment} from 'moment';
import React from 'react';
import styled from 'styled-components';
import {ExtraFilter} from './types';

const extraFilters: ExtraFilter[] = [
  'paymentMethodBizumIntegrationType',
  'paymentMethodCardBrand',
  'amountOperator'
];

type FilterValues = {
  filterName: string;
  filterValue: string;
  createdAt: [Moment, Moment];
} & Partial<Record<ExtraFilter, string>>;

export type FilterQueryParams = {
  createdAt?: string[];
  [key: string]: any;
};

type Props = {
  onChange: (values: FilterQueryParams) => void;
  queryFilters: FilterQueryParams;
  accountId?: string;
  stores: StoreFieldsFragment[];
  availableFilters?: Record<string, string>;
};

const adminOnlyFilters = ['acquirer', 'processor', 'masterAccountId', 'billingPlan'];

const RangePicker = styled(DatePicker.RangePicker)`
  min-width: 240px;
`;

export const PaymentFilters: React.FC<Props> = ({
  onChange,
  queryFilters,
  accountId,
  stores,
  availableFilters
}) => {
  const {isAdmin} = useAccessLevel();
  const {createdAt = [], ...filters} = queryFilters;

  const defaultFilterName = accountId ? 'paymentMethodMethod' : 'shopName';

  const filterName =
    Object.keys(availableFilters || {})
      .filter(
        (key) => !extraFilters.includes(key as ExtraFilter) && !(accountId && key === 'accountId')
      )
      .find(
        (key) => !filters.hasOwnProperty(key) || filters[key] === undefined || filters[key] === null
      ) ?? '';

  const initialValues: FilterValues = {
    filterName: filters[defaultFilterName] ? filterName : defaultFilterName,
    filterValue: '',
    createdAt: [moment(createdAt[0]).startOf('day'), moment(createdAt[1]).endOf('day')]
  };

  if (filterName === 'amount') {
    initialValues.amountOperator = filters.amountOperator || 'eq';
  }

  const storeOptions = stores.map((store) => ({
    label: store.name as string,
    value: store.id
  }));

  Object.values(extraFilters).forEach((val) => {
    initialValues[val as ExtraFilter] = filters[val] || undefined;
  });

  const handleSubmit = (
    {createdAt, filterName, filterValue, ...extra}: FilterValues,
    {resetForm}: FormikHelpers<typeof initialValues>
  ) => {
    const [startMoment, endMoment] = createdAt;
    const start = startMoment ? startMoment : moment().subtract(1, 'day');
    const end = endMoment ? endMoment : moment();
    const filter: FilterQueryParams = {...filters};

    if (filterValue !== '') filter[filterName] = filterValue;

    filter.createdAt = [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
    if (filterName === 'paymentMethodMethod') {
      filter.paymentMethodBizumIntegrationType =
        filterValue === PaymentMethods.BIZUM && extra.paymentMethodBizumIntegrationType !== 'all'
          ? extra.paymentMethodBizumIntegrationType
          : undefined;
    }
    if (['paymentMethodMethod', 'paymentMethodCardTokenizationMethod'].includes(filterName)) {
      filter.paymentMethodCardBrand =
        [
          PaymentMethods.CARD,
          PaymentMethods.CLICKTOPAY,
          PaymentMethods.APPLEPAY,
          PaymentMethods.GOOGLEPAY
        ].includes(filterValue as PaymentMethods) && extra.paymentMethodCardBrand !== 'all'
          ? extra.paymentMethodCardBrand
          : undefined;
    }
    if (filterName === 'amount') {
      filter.amount = amountToInt(+filterValue);
      filter.amountOperator = extra.amountOperator;
    }

    setTimeout(resetForm, 300);
    return onChange(filter);
  };

  const isSelected = (value: string) =>
    queryFilters.hasOwnProperty(value) &&
    queryFilters[value] !== undefined &&
    queryFilters[value] !== null;

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({dirty, submitForm, values}) => (
        <Form>
          <SearchBar compact>
            <Select
              name="filterName"
              style={{minWidth: 250}}
              showSearch
              optionFilterProp="children"
              listHeight={500}>
              {Object.entries(availableFilters || {}).map(([value, label]) => {
                if ((adminOnlyFilters.includes(value) && !isAdmin) || isSelected(value))
                  return null;
                return (
                  <Select.Option key={value} value={value}>
                    {label}
                  </Select.Option>
                );
              })}
            </Select>
            <FilterInput
              filterName={values.filterName}
              filterValue={values.filterValue}
              filterOptions={storeOptions}
            />
            <RangePicker
              name="createdAt"
              format="L"
              allowClear={false}
              onOpenChange={(isOpen) => !isOpen && submitForm()}
              disabledDate={(date) => (date ? date.isAfter(moment().endOf('day')) : false)}
              ranges={{
                Today: [moment().startOf('day'), moment().endOf('day')],
                'Last week': [moment().startOf('day').subtract(7, 'days'), moment().endOf('day')],
                'Last 14 days': [
                  moment().startOf('day').subtract(14, 'days'),
                  moment().endOf('day')
                ],
                'Last month': [moment().startOf('day').subtract(1, 'month'), moment().endOf('day')],
                'Last 3 months': [
                  moment().startOf('day').subtract(3, 'month'),
                  moment().endOf('day')
                ]
              }}
            />
            <Button htmlType="submit" style={{minWidth: 130}} type={dirty ? 'primary' : undefined}>
              {dirty ? (
                <>
                  <SearchOutlined /> Search
                </>
              ) : (
                <>
                  <ReloadOutlined /> Refresh
                </>
              )}
            </Button>
          </SearchBar>
        </Form>
      )}
    </Formik>
  );
};
